/**
 * The deck (#1282) — three swipes fill three days.
 *
 * Pointer events, not touch events: one code path covers finger, mouse and
 * pen, and it is the only way the same gesture works on a laptop demo and a
 * phone in a kitchen.
 *
 * Three things a card deck gets wrong if you are not careful:
 *
 *   A VERTICAL DRAG MUST SCROLL THE PAGE. The card is taller than the phone —
 *   it has two plates on it. Capturing every gesture makes the card unreadable
 *   in order to make it swipeable, which is the wrong trade. The axis is
 *   decided once, at the point the finger has moved far enough to mean
 *   something, and never revisited during that gesture.
 *
 *   THE STAMP FADES IN WITH THE GESTURE. A commit that appears only on release
 *   gives no chance to change your mind mid-drag, which is most of what a
 *   swipe is for.
 *
 *   BUTTONS ARE NOT A FALLBACK. Plenty of people are not on a phone, and
 *   somebody with a tremor should not have to fight a gesture to plan dinner.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { twoPlates, type OfferedMeal } from "../api/twoPlates";
import { MealCard } from "../components/MealCard";

/** How far before a drag counts as a decision rather than a wobble. */
const COMMIT_PX = 90;
/** How far before the axis is decided. Below this a gesture means nothing yet. */
const AXIS_PX = 12;

type Axis = "undecided" | "horizontal" | "vertical";

export default function Deck() {
  const navigate = useNavigate();
  const [chosen, setChosen] = useState<OfferedMeal[]>([]);
  const chosenIds = chosen.map((m) => m.mealId);

  // Refetched as the choices change, so the re-ranking rule stays in the engine
  // rather than being reimplemented here. keepPreviousData means the card under
  // the finger does not blink between swipes.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["deck", chosenIds],
    queryFn: () => twoPlates.deck(chosenIds),
    placeholderData: (prev) => prev,
  });

  // How many nights this household plans, 3 to 7. Read from the server rather
  // than assumed, so changing it in Kitchen changes the deck immediately.
  const daysToFill = data?.household.planDays ?? 3;

  const [passed, setPassed] = useState<number[]>([]);
  const [showDropped, setShowDropped] = useState(false);

  const queue = useMemo(() => {
    if (!data) return [];
    const decided = new Set([...chosen.map((m) => m.mealId), ...passed]);
    return data.offered.filter((m) => !decided.has(m.mealId));
  }, [data, chosen, passed]);

  const current = queue[0];

  const choose = useCallback(
    (meal: OfferedMeal) => setChosen((prev) => (prev.length < daysToFill ? [...prev, meal] : prev)),
    [daysToFill],
  );
  const pass = useCallback((meal: OfferedMeal) => setPassed((prev) => [...prev, meal.mealId]), []);

  if (isLoading) return <Shell><p className="text-muted text-sm">Building your deck…</p></Shell>;
  if (isError) {
    return (
      <Shell>
        <p className="text-warn text-sm">{(error as Error)?.message ?? "Could not load the deck"}</p>
      </Shell>
    );
  }
  if (!data) return null;

  if (chosen.length >= daysToFill) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold tracking-tight">
          {daysToFill} nights, sorted.
        </h1>
        <ol className="mt-6 flex flex-col gap-3">
          {chosen.map((m, i) => (
            <li key={m.mealId} className="bg-surface border-line rounded-xl border p-4">
              <p className="text-muted text-xs uppercase tracking-wide">Day {i + 1}</p>
              <p className="mt-1 font-semibold">{m.name}</p>
              <p className="text-muted mt-1 text-sm">
                {m.anchor}
                {m.sides.length > 0 && ` with ${m.sides.join(", ")}`}
              </p>
            </li>
          ))}
        </ol>
        <button
          onClick={() => navigate(`/plan?meals=${chosen.map((m) => m.mealId).join(",")}`)}
          className="bg-plate-b text-ground tap mt-7 w-full rounded-lg font-semibold"
        >
          Build the plan and the list
        </button>
        <button
          onClick={() => setChosen([])}
          className="text-muted mt-4 text-sm underline underline-offset-4"
        >
          Start over
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="mb-5 flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">What are we eating?</h1>
          <p className="text-muted mt-1 text-xs">
            {chosen.length} of {daysToFill} nights chosen
          </p>
        </div>
        <div className="flex gap-1.5" aria-hidden>
          {Array.from({ length: daysToFill }, (_, i) => (
            <span
              key={i}
              className="h-1.5 w-6 rounded-full"
              style={{ background: i < chosen.length ? "var(--color-plate-b)" : "var(--color-line)" }}
            />
          ))}
        </div>
      </header>

      {current ? (
        <SwipeCard
          key={current.mealId}
          meal={current}
          onChoose={() => choose(current)}
          onPass={() => pass(current)}
          remaining={queue.length}
        />
      ) : (
        <div className="bg-surface border-line rounded-2xl border p-6">
          <p className="font-semibold">That is every meal you can eat.</p>
          <p className="text-muted mt-2 text-sm">
            {data.offered.length} of {data.offered.length + data.dropped.length} in the library
            work for this household. Take another pass, or loosen a preference.
          </p>
          <button
            onClick={() => setPassed([])}
            className="text-plate-b mt-4 text-sm underline underline-offset-4"
          >
            Show them again
          </button>
          <button
            onClick={() => navigate("/import")}
            className="text-muted mt-2 block text-sm underline underline-offset-4"
          >
            Or add one of your own
          </button>
        </div>
      )}

      {data.dropped.length > 0 && (
        <section className="mt-8">
          <button
            onClick={() => setShowDropped((v) => !v)}
            className="text-muted text-sm underline underline-offset-4"
            aria-expanded={showDropped}
          >
            {showDropped ? "Hide" : `Why ${data.dropped.length} meals are not here`}
          </button>
          {showDropped && (
            <ul className="mt-4 flex flex-col gap-2.5">
              {data.dropped.map((d) => (
                <li key={d.mealId} className="border-line rounded-lg border p-3">
                  <p className="text-sm font-medium">{d.name}</p>
                  <p className="text-muted mt-0.5 text-xs">{d.reason}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </Shell>
  );
}

function SwipeCard({
  meal,
  onChoose,
  onPass,
  remaining,
}: {
  meal: OfferedMeal;
  onChoose: () => void;
  onPass: () => void;
  remaining: number;
}) {
  const [dx, setDx] = useState(0);
  const axis = useRef<Axis>("undecided");
  const start = useRef<{ x: number; y: number } | null>(null);

  const commit = Math.min(1, Math.abs(dx) / COMMIT_PX);

  function down(e: React.PointerEvent) {
    // Never hijack a gesture that began on a control.
    if ((e.target as HTMLElement).closest("button, a")) return;
    start.current = { x: e.clientX, y: e.clientY };
    axis.current = "undecided";
  }

  function move(e: React.PointerEvent) {
    if (!start.current) return;
    const deltaX = e.clientX - start.current.x;
    const deltaY = e.clientY - start.current.y;

    if (axis.current === "undecided") {
      if (Math.abs(deltaX) < AXIS_PX && Math.abs(deltaY) < AXIS_PX) return;
      // Decided once and never revisited: a gesture that changes its mind
      // halfway is one that both scrolls and throws, which feels broken.
      axis.current = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
      if (axis.current === "horizontal") e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (axis.current !== "horizontal") return;
    setDx(deltaX);
  }

  function up() {
    if (axis.current === "horizontal" && Math.abs(dx) >= COMMIT_PX) {
      const chose = dx > 0;
      setDx(0);
      start.current = null;
      axis.current = "undecided";
      chose ? onChoose() : onPass();
      return;
    }
    setDx(0);
    start.current = null;
    axis.current = "undecided";
  }

  return (
    <div>
      <div
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        className="relative select-none"
        style={{
          transform: `translateX(${dx}px) rotate(${dx / 40}deg)`,
          // Only settle with a transition; during the drag it must track the
          // finger exactly or the card feels laggy rather than physical.
          transition: dx === 0 ? "transform 200ms ease-out" : "none",
          // The browser keeps vertical panning; we only claim the X axis.
          touchAction: "pan-y",
        }}
      >
        <MealCard meal={meal} />

        <span
          aria-hidden
          className="pointer-events-none absolute left-5 top-5 rounded-lg border-2 px-3 py-1 text-sm font-bold uppercase tracking-widest"
          style={{
            opacity: dx > 0 ? commit : 0,
            color: "var(--color-plate-b)",
            borderColor: "var(--color-plate-b)",
            transform: `rotate(-8deg) scale(${0.9 + commit * 0.1})`,
          }}
        >
          Cooking it
        </span>
        <span
          aria-hidden
          className="pointer-events-none absolute right-5 top-5 rounded-lg border-2 px-3 py-1 text-sm font-bold uppercase tracking-widest"
          style={{
            opacity: dx < 0 ? commit : 0,
            color: "var(--color-muted)",
            borderColor: "var(--color-muted)",
            transform: `rotate(8deg) scale(${0.9 + commit * 0.1})`,
          }}
        >
          Not this week
        </span>
      </div>

      <div className="mt-5 flex gap-3">
        <button onClick={onPass} className="border-line tap flex-1 rounded-xl border font-medium">
          Not this week
        </button>
        <button
          onClick={onChoose}
          className="bg-plate-b text-ground tap flex-1 rounded-xl font-semibold"
        >
          Cooking it
        </button>
      </div>
      <p className="text-muted mt-3 text-center text-xs">
        {remaining - 1} more if you want them · swipe or tap
      </p>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  // The app chrome owns the page padding now (#1323); this is only a grouping.
  return <>{children}</>;
}
